<script setup>
    import { reactive, computed } from 'vue'
    import algoliasearch from 'algoliasearch/lite'
    import InsightItem from './partials/InsightItem.vue'

    const state = reactive({
        searchClient: algoliasearch(
            window.ALGOLIA_APP_ID,
            window.ALGOLIA_PUBLIC
        )
    })

    const data = reactive({
        filtersOpen: false
    })

    const props = defineProps({
        searchIndex: {
            type: String,
            required: true
        },
        showFilters: {
            type: Boolean,
            required: false,
            default: () => { return false }
        },
        filters: {
            type: Array,
            required: false,
            default: () => { return [] }
        }
    })

    const searchIndex = computed(() => {
        const prefix = window?.ALGOLIA_USE_DEV_INDEXES === true ? 'dev_' : 'prod_'
        return `${prefix}${props.searchIndex}`
    })

    const showFilters = computed(() => {
        return props.filters.length > 0
    })

    function toggleFilters () {
        data.filtersOpen = !data.filtersOpen
    }
</script>
<template>
    <ais-instant-search
        ref="algoliaSearch"
        :search-client="state.searchClient"
        :index-name="searchIndex"
        :stalled-search-delay="250"
    >
        <slot name="config" />
        <div class="block-inner grid gap-8 md:grid-cols-1 lg:grid-cols-5 py-20">
            <aside
                v-if="showFilters"
                class="flex items-start lg:col-span-1"
            >
                <div class="bg-white shadow-lg w-full lg:max-w-sm mb-8 md:mb-0">
                    <h3 class="hidden md:block text-2xl font-bold px-6 py-4 m-0">
                        Filters
                    </h3>
                    <button
                        type="button"
                        class="flex justify-between items-center md:hidden w-full text-left appearance-none px-6 py-4"
                        @click.prevent="toggleFilters"
                    >
                        <h3 class="text-2xl font-bold m-0">
                            Filters
                        </h3>
                        <i
                            :class="{
                                'fa-sharp fa-solid fa-caret-down text-cintra-orange fa-2x transition transform ease-in-out': true,
                                'rotate-0': !data.filtersOpen,
                                'rotate-180': data.filtersOpen
                            }"
                        />
                    </button>
                    <div
                        :class="{
                            'w-full px-6 py-4': true,
                            'block': data.filtersOpen,
                            'hidden md:block': !data.filtersOpen
                        }"
                    >
                        <ais-refinement-list
                            v-for="(filter, index) in filters"
                            :key="index"
                            :attribute="filter.key"
                            operator="and"
                            class="w-full mb-6"
                        >
                            <template v-slot="{ items, refine, createURL }">
                                <template v-if="items.length > 0">
                                    <h4
                                        v-if="filter.label"
                                        class="text-base font-bold mt-0 mb-2"
                                    >
                                        {{ filter.label }}
                                    </h4>
                                    <a
                                        v-for="item in items"
                                        :key="item.value"
                                        :href="createURL(item)"
                                        :class="{
                                            'flex items-center space-x-2': true,
                                            'text-gray-700': !item.isRefined,
                                            'text-black': item.isRefined
                                        }"
                                        @click.prevent="refine(item.value)"
                                    >
                                        <input
                                            :checked="item.isRefined"
                                            type="checkbox"
                                            class="custom-checkbox mr-3"
                                            aria-hidden="true"
                                            tabindex="-1"
                                        >
                                        <div class="flex-grow flex items-center justify-between w-full">
                                            <ais-highlight
                                                :hit="item"
                                                attribute="item"
                                            />
                                        </div>
                                    </a>
                                </template>
                            </template>
                        </ais-refinement-list>
                        <ais-clear-refinements class="mt-8">
                            <template v-slot="{ canRefine, refine, createURL }">
                                <div v-show="canRefine">
                                    <button
                                        :href="createURL()"
                                        type="button"
                                        class="inline-block w-full lg:w-auto text-center rounded-full shadow border-2 border-cintra-orange appearance-none px-8 py-2"
                                        @click.prevent="refine"
                                    >
                                        Clear filters
                                    </button>
                                </div>
                            </template>
                        </ais-clear-refinements>
                    </div>
                </div>
            </aside>
            <div
                :class="{
                    'lg:col-span-4': showFilters,
                    'lg:col-span-full': !showFilters
                }"
            >
                <div class="w-full flex flex-col lg:flex-row mb-8">
                    <ais-stats class="w-full lg:w-auto text-xl mr-8 mt-1.5 mb-4 lg:mb-0">
                        <template v-slot="{ nbHits }">
                            <span class="font-bold">{{ nbHits }}</span> {{ nbHits === 1 ? 'Result' : 'Results' }}
                        </template>
                    </ais-stats>
                    <ais-current-refinements v-if="showFilters" class="flex-grow flex flex-wrap">
                        <template v-slot="{ items, createURL }">
                            <div
                                v-for="item in items"
                                :key="item.attribute"
                                class="flex flex-wrap"
                            >
                                <div
                                    v-for="refinement in item.refinements"
                                    :key="[
                                        refinement.attribute,
                                        refinement.type,
                                        refinement.value,
                                        refinement.operator
                                    ].join(':')"
                                    class="flex flex-wrap"
                                >
                                    <a
                                        :href="createURL(refinement)"
                                        class="flex justify-between items-center min-w-[100px] bg-black text-white rounded-full space-x-4 shadow pl-8 pr-6 py-2 mr-2 mb-2"
                                        @click.prevent="item.refine(refinement)"
                                    >
                                        <span>{{ refinement.label }}</span>
                                        <i class="fa-sharp fa-solid fa-times text-cintra-orange pointer-events-none mb-px"></i>
                                    </a>
                                </div>
                            </div>
                        </template>
                    </ais-current-refinements>
                </div>
                <ais-hits
                    :class="{
                        'grid grid-cols-1 gap-4 w-full': true,
                        'md:grid-cols-3 xl:grid-cols-3 xl:grid-cols-3': showFilters,
                        'md:grid-cols-3': !showFilters
                    }"
                >
                    <template v-slot="{ items }">
                        <InsightItem
                            v-for="item in items"
                            :key="item.objectID"
                            :item="item"
                        >
                            <template #title>
                                <ais-highlight
                                    :hit="item"
                                    attribute="title"
                                />
                            </template>
                        </InsightItem>
                    </template>
                </ais-hits>
                <ais-pagination :padding="2" :total-pages="5">
                    <template v-slot="{ currentRefinement, nbPages, pages, isFirstPage, isLastPage, refine, createURL }">
                        <ul
                            class="text-2xl md:text-lg mt-8 flex items-center gap-2"
                            :class="{
                                'hidden': pages.length <= 1,
                                'flex items-center justify-center': pages.length > 1,
                            }"
                        >
                            <li class="h-8 flex items-center">
                                <button
                                    :disabled="isFirstPage"
                                    :href="createURL(0)"
                                    aria-label="Navigate to the first page"
                                    type="button"
                                    class="disabled:opacity-30 disabled:pointer-events-none rounded-full transition duration-300 border border-cintra-grey hover:border-cintra-orange w-8 h-8 relative group"
                                    @click.prevent="refine(0)"
                                >
                                    <i class="fa-light fa-chevrons-left text-cintra-grey transition duration-300 group-hover:text-cintra-orange absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></i>
                                </button>
                            </li>
                            <li class="h-8 flex items-center">
                                <button
                                    :disabled="isFirstPage"
                                    :href="createURL(currentRefinement - 1)"
                                    aria-label="Navigate to previous page"
                                    type="button"
                                    class="disabled:opacity-30 disabled:pointer-events-none rounded-full transition duration-300 border border-cintra-grey hover:border-cintra-orange w-8 h-8 relative group"
                                    @click.prevent="refine(currentRefinement - 1)"
                                >
                                    <i class="fa-light fa-chevron-left text-cintra-grey transition duration-300 group-hover:text-cintra-orange absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></i>
                                </button>
                            </li>
                            <li class="px-4 md:px-2">
                                <span class="font-bold">{{ currentRefinement + 1 }}</span> / {{ nbPages }}
                            </li>
                            <li class="h-8 flex items-center">
                                <button
                                    :disabled="isLastPage"
                                    :href="createURL(currentRefinement + 1)"
                                    aria-label="Navigate to next page"
                                    type="button"
                                    class="disabled:opacity-30 disabled:pointer-events-none rounded-full transition duration-300 border border-cintra-grey hover:border-cintra-orange w-8 h-8 relative group"
                                    @click.prevent="refine(currentRefinement + 1)"
                                >
                                    <i class="fa-light fa-chevron-right text-cintra-grey transition duration-300 group-hover:text-cintra-orange absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></i>
                                </button>
                            </li>
                            <li class="h-8 flex items-center">
                                <button
                                    :disabled="isLastPage"
                                    :href="createURL(nbPages)"
                                    aria-label="Navigate to the last page"
                                    type="button"
                                    class="disabled:opacity-30 disabled:pointer-events-none rounded-full transition duration-300 border border-cintra-grey hover:border-cintra-orange w-8 h-8 relative group"
                                    @click.prevent="refine(nbPages)"
                                >
                                    <i class="fa-light fa-chevrons-right text-cintra-grey transition duration-300 group-hover:text-cintra-orange absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></i>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ais-pagination>
            </div>
        </div>
    </ais-instant-search>
</template>
<style lang="scss">

</style>
